<template>
  <help-info-wrapper
    :info.sync="helpData"
    :service-name="serviceName"
    class="warp"
  >
    <template>
      <div>
        <slot name="default">
          <div class="rowinfo">
            <v-text-field
              :label="
                $attrs['label'] ? $t($attrs['label']) : $t('select_category')
              "
              :single-line="false"
              :value="displayValue"
              class="rounded-lg"
              clearable
              dense
              hide-details
              outlined
              readonly
              @click="dialog = true"
              @keyup.enter="dialog = false"
              @click:clear="selectedItems = []"
            />
            <help-info
              :element-name="`${serviceName}-${componentName}-displayValue-${
                $attrs['label'] ? $t($attrs['label']) : $t('select_category')
              }`"
              :service-name="serviceName"
              :help-data="helpData"
              class="buttoninfo"
              :label="
                $attrs['label'] ? $t($attrs['label']) : $t('select_category')
              "
            />
          </div>
        </slot>

        <custom-popup v-model="dialog" :cons="cons" max-width="640">
          <v-card class="pa-4" flat>
            <div class="sticky-top white pb-4" style="top: 68px">
              <v-card-title class="justify-end pa-0">
                <div class="rowinfo" :disabled="!selectedItems.length">
                  <v-btn
                    :disabled="!selectedItems.length"
                    class="rounded-lg"
                    color="red"
                    text
                    @click="selectedItems = []"
                  >
                    {{ $t("delete_all") }}
                  </v-btn>
                  <help-info
                    :element-name="`${serviceName}-${componentName}-delete-product-selectedItems`"
                    :service-name="serviceName"
                    :help-data="helpData"
                    class="buttoninfo"
                    :label="$t('delete_all')"
                  />
                </div>
              </v-card-title>

              <v-chip-group column multiple>
                <v-chip
                  v-for="(chip, i) in selectedItems"
                  :key="i"
                  :value="chip.id"
                  class="rounded-pill"
                  close
                  color="red"
                  outlined
                  small
                  @click:close="removeItem(chip)"
                >
                  {{ chip.name }}
                </v-chip>
              </v-chip-group>

              <div class="my-4">
                <div class="rowinfo">
                  <v-text-field
                    v-model="searching"
                    clearable
                    dense
                    hide-details
                    class="contentinfo"
                    :label="$t('search_categories')"
                    outlined
                  />
                  <help-info
                    :element-name="`${serviceName}-${componentName}-searching`"
                    :service-name="serviceName"
                    :help-data="helpData"
                    class="buttoninfo"
                    :label="$t('search_categories')"
                  />
                </div>
              </div>
            </div>

            <template v-if="!!search && search.length > 1">
              <template v-for="(cat, i) in searchedItems">
                <v-list-item :key="i" dense @click="selectSearched(cat)">
                  <v-list-item-avatar size="42">
                    <v-img
                      v-if="
                        !!cat.data &&
                        !!cat.data.icon &&
                        !!cat.data.icon.image_url_light
                      "
                      :src="cat.data.icon.image_url_light"
                    />
                    <v-icon v-else>mdi-view-grid-outline</v-icon>
                  </v-list-item-avatar>

                  <v-list-item-content>
                    <v-list-item-title
                      style="font-size: 1.2em; line-height: 1.2em"
                    >
                      {{ cat.name }}
                    </v-list-item-title>
                  </v-list-item-content>

                  <v-list-item-action>
                    <v-btn v-if="multiple" icon>
                      <v-icon
                        >mdi-{{
                          getChecked(cat)
                            ? "checkbox-marked"
                            : "checkbox-blank-outline"
                        }}</v-icon
                      >
                    </v-btn>
                  </v-list-item-action>
                </v-list-item>
              </template>
            </template>

            <template v-else>
              <v-list v-if="currentCategories" dense>
                <v-list-item v-if="!!parentCategory" @click="goBack">
                  <v-list-item-action>
                    <v-icon>mdi-arrow-right</v-icon>
                  </v-list-item-action>

                  <v-list-item-content>
                    <v-list-item-title
                      style="font-size: 1.2em; line-height: 1.2em"
                    >
                      {{ parentCategory.name }}
                    </v-list-item-title>
                  </v-list-item-content>
                </v-list-item>

                <template v-for="(cat, i) in currentCategories">
                  <v-list-item :key="i" dense @click="showChildren(cat)">
                    <v-list-item-avatar size="42">
                      <v-img
                        v-if="
                          !!cat.data &&
                          !!cat.data.icon &&
                          !!cat.data.icon.image_url_light
                        "
                        :src="cat.data.icon.image_url_light"
                      />
                      <v-icon v-else>mdi-view-grid-outline</v-icon>
                    </v-list-item-avatar>

                    <v-list-item-content>
                      <v-list-item-title
                        style="font-size: 1.2em; line-height: 1.2em"
                      >
                        {{ cat.name }}
                      </v-list-item-title>
                    </v-list-item-content>

                    <v-list-item-action>
                      <v-icon v-if="!!cat.children?.length"
                        >mdi-chevron-left</v-icon
                      >
                      <v-btn v-else-if="multiple" icon>
                        <v-icon
                          >mdi-{{
                            getChecked(cat)
                              ? "checkbox-marked"
                              : isIndeterminate(cat, i)
                              ? "minus-box"
                              : "checkbox-blank-outline"
                          }}
                        </v-icon>
                      </v-btn>
                    </v-list-item-action>
                  </v-list-item>
                </template>
              </v-list>
            </template>
          </v-card>
        </custom-popup>
      </div>
    </template>
  </help-info-wrapper>
</template>

<script>
import { mapState } from "vuex";
import { debounce } from "@/main";

export default {
  name: "personalCategoryPicker",

  props: {
    value: [Array, Object, String],
    multiple: Boolean,
    returnObject: Boolean,
  },

  data() {
    return {
      helpData: [],
      componentName: "personalCategoryPicker",
      serviceName: "Qrland",
      dialog: false,
      loading: false,
      currentCategories: [],
      items: [],
      parentCategory: null,
      selectedItems: [],
      searching: "",
      search: "",
    };
  },

  created() {
    this.fetchCategories();
  },

  computed: {
    ...mapState("localstorage", ["createQrID"]),
    cons() {
      return {
        title: this.$attrs["label"]
          ? this.$t(this.$attrs["label"])
          : this.$t("select_category"),
        buttons: [
          { type: "submit", handler: this.submitSelect },
          {
            type: "close",
            handler: () => {
              this.currentCategories = this.items;
              this.parentCategory = null;
              this.dialog = false;
            },
          },
        ],
      };
    },
    displayValue() {
      let arr = [];
      if (this.value instanceof Array) arr = this.value;

      return [...this.selectedItems.map((item) => item.name), ...arr].join(
        ", "
      );
    },
    searchedItems() {
      if (this.search?.length < 2) return [];

      let items = [];
      const getChildren = (category) => {
        if (!!category.children?.length)
          category.children.forEach((item) => {
            getChildren(item);
          });
        else items.push(category);
      };

      this.items.forEach((item) => getChildren(item));
      return items.filter((data) =>
        data.name.toLowerCase().includes(this.search.toLowerCase())
      );
    },
  },

  watch: {
    "$i18n.locale": {
      handler() {
        this.$forceUpdate();
      },
      immediate: true,
      deep: true,
    },
    searching(val) {
      debounce(() => {
        this.search = val;
      }, 1200);
    },
  },

  methods: {
    fetchCategories() {
      this.loading = true;

      this.$api
        .get("/v1/testQr/category-menus", {
          params: {
            qr_id: this.createQrID,
          },
        })
        .then(({ data }) => {
          this.items = JSON.parse(JSON.stringify(data.data));
          this.currentCategories = data.data;
        })
        .catch(({ response }) =>
          console.log("failed to get category menus: ", response)
        )
        .finally(() => (this.loading = false));
    },
    submitSelect() {
      let value = null;

      if (this.multiple) {
        if (this.returnObject) {
          value = this.selectedItems;
        } else {
          value = this.selectedItems.map((item) => item.id);
        }
      } else if (this.returnObject) value = this.selectedItems;
      else value = this.selectedItems?.[0].id;

      this.$emit("input", value);
      this.$emit("change", value);
      this.currentCategories = this.items;
      this.parentCategory = null;
    },
    selectSearched(category) {
      const { id, name } = category;

      if (!this.multiple) {
        this.selectedItems = [{ id, name }];
      } else {
        const isItemSelected = this.selectedItems.findIndex(
          (item) => item.id === id
        );

        if (isItemSelected < 0) {
          this.selectedItems.push({ id, name });
        } else {
          this.selectedItems.splice(isItemSelected, 1);
        }
      }
    },
    showChildren(category) {
      if (category.children?.length) {
        this.parentCategory = category;
        this.currentCategories = category.children;
      } else {
        const { id, name } = category;

        if (!this.multiple) {
          this.selectedItems = [{ id, name }];
        } else {
          this.handleMultiple(category);
        }
      }
    },
    handleMultiple(category) {
      const { id, name } = category;

      const isItemSelected = this.selectedItems.some((item) => item.id === id);
      const hasParent = this.selectedItems.some((item) => id === item.id);
      const isParent = id === this.parentCategory?.id;

      if (isParent && !isItemSelected) {
        this.selectedItems = [
          ...this.selectedItems.reduce((arr, curr) => {
            if (!curr.id.includes(id)) arr.push(curr);
            return arr;
          }, []),
          { id, name },
        ];
      } else if (isItemSelected) {
        const parentItemIndex = this.selectedItems.findIndex(
          (item) => item.id === id
        );
        this.selectedItems.splice(parentItemIndex, 1);
        this.retainHigherLevelCategories(this.parentCategory);
      } else if (hasParent) {
        const parentIndex = this.selectedItems.findIndex((item) =>
          id.includes(item.id)
        );

        if (parentIndex >= 0) {
          this.selectedItems.splice(parentIndex, 1);
        }

        const siblings = this.parentCategory.children
          .slice(1)
          .filter((item) => item.data.id !== id)
          .map((item) => ({
            id: item.id,
            name: item.name,
          }));

        this.selectedItems.push(...siblings);
      } else if (!isItemSelected) {
        this.selectedItems.push({ id, name });
      } else {
        this.selectedItems.splice(
          this.selectedItems.findIndex((item) => item.id === id),
          1
        );
      }

      if (
        this.parentCategory?.children
          ?.slice(1)
          .every((item) => this.selectedItems.some((si) => si.id === item.id))
      ) {
        const p = { pv: this.parentCategory.id, pd: this.parentCategory.name };
        this.selectedItems = [
          ...this.selectedItems.reduce((arr, curr) => {
            if (!curr.id.includes(this.parentCategory.id)) arr.push(curr);
            return arr;
          }, []),
          { id: p.pv, name: p.pd },
        ];
      }
    },
    retainHigherLevelCategories(category) {
      let parent = this.findParent(category);
      if (!parent) return;

      const parentValue = parent.id;
      const parentDisplay = parent.name;

      if (!this.selectedItems.some((item) => item.id === parentValue)) {
        this.selectedItems.push({ id: parentValue, name: parentDisplay });
      }

      this.retainHigherLevelCategories(parent);
    },
    goBack() {
      if (this.parentCategory) {
        const parent = this.findParent(this.parentCategory);
        if (parent) {
          this.currentCategories = parent.children;
          this.parentCategory = parent;
        } else {
          this.currentCategories = this.items;
          this.parentCategory = null;
        }
      }
    },
    findParent(current) {
      const findRecursively = (categories) => {
        for (const category of categories) {
          if (category.children?.includes(current)) {
            return category;
          }

          if (!!category.children?.length) {
            const parent = findRecursively(category.children);
            if (parent) return parent;
          }
        }

        return null;
      };

      return findRecursively(this.items);
    },
    removeItem(chip) {
      let index = this.selectedItems.findIndex((item) => item.id === chip.id);
      this.selectedItems.splice(index, 1);
    },
    getChecked(current) {
      let pv = this.parentCategory?.id;

      return (
        !!this.selectedItems?.map((item) => item.id).includes(current.id) ||
        this.selectedItems?.some((item) => item.id === pv)
      );
    },
    isIndeterminate(category, index) {
      return (
        index === 0 &&
        !this.selectedItems?.some((item) => item.id === category.id) &&
        !!this.selectedItems?.some(
          (item) => item.id === this.parentCategory?.id
        )
      );
    },
  },
};
</script>

<style scoped>
/* پشتیبانی از زبان‌های راست به چپ (RTL) مانند فارسی و عربی */
:deep(.v-application--is-rtl) {
  direction: rtl;
}
</style>
